import React, { type ChangeEvent, useState, useEffect, useRef } from 'react'
import { useSpeechRecognition } from 'react-speech-recognition'
import { ThreeDots } from 'react-loader-spinner'
import {
  Button,
  Stack,
  Typography,
  CircularProgress,
  InputBase,
  type InputBaseProps,
  styled,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Box,
  Card,
  CardContent,
  TextField,
  Chip,
} from '@mui/material'
import type { AlertColor } from '@mui/material/Alert'
import { useNavigate } from 'react-router-dom'
import MicPhoneButton from './MicPhoneButton'
// import InputMessage from './InputMessage'
import { CloseIcon, SendIcon } from '../../../../components/Icons'
import { useSidebarContext } from '../../../../contexts/Old_SidebarContext'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import { useSnackbar } from '../../../../components/SnackbarProvider'

import baseUrl from '../../../../config/baseUrl'
import { FileUpload } from '../../../../components/FileUpload/index'
import LoadingMessages from './loadingMessages/LoadingMessages'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import StopIcon from '@mui/icons-material/Stop'
import DoneIcon from '@mui/icons-material/Done'
import HearingIcon from '@mui/icons-material/Hearing'
import SpeechToText from './SpeechToText'

const StyledInputBase = styled(InputBase)<InputBaseProps>(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  borderWidth: 0.5,
  borderColor: theme.palette.text.primary,
  '& .MuiInputAdornment-positionEnd': {
    paddingRight: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      paddingRight: theme.spacing(0), // Smaller padding for 'sm' screen size
    },
  },
  '& .MuiInputBase-input': {
    [theme.breakpoints.down('sm')]: {
      padding: '13px 5px', // Adjust padding for mobile if needed
    },
  },
}))

interface Props {
  setInputMessage: React.Dispatch<React.SetStateAction<string>>
  setAnswer: React.Dispatch<React.SetStateAction<string>>
  aiStorage: any[] // Assuming aiStorage is an array of objects
  setAiStorage: React.Dispatch<React.SetStateAction<any[]>>
}
const SendMessage = ({ setInputMessage, setAnswer, aiStorage, setAiStorage }: Props) => {
  const { transcript } = useSpeechRecognition()
  // const [message, setMessage] = useState<string>('')
  const {
    answer,
    openSpeechToText,
    setOpenSpeechToText,
    uniqueString,
    sendIsLoading,
    setSendIsLoading,
    setUniqueString,
    deleteUniqueString,
    isRefFileUploaded,
    setIsRefFileUploaded,
    uploadedFileName,
    setUploadedFileName,
    // uniqueString,
    isLoading,

    newChatClicked,
    setNewChatClicked,
    userStatus,
    handleSearchCount,
    sendData,
    inputMessage,
    message,
    setMessage,
    setLastAskedQuestion,
    isFileUploading,
    showFileUploadBox,
    setShowFileUploadBox,

    setAreFilesSelected,
    isModelBuilding,
    setIsModelBuilding,
  } = useSidebarContext()

  const promptRef = useRef<HTMLInputElement>(null)
  const dummyRef = useRef<HTMLInputElement>(null)

  const userId: number = sessionStorage.getItem('userId') as unknown as number

  // speech recognition

  const [placeholder, setPlaceholder] = useState('Enter a prompt here')

  const [isListening, setIsListening] = useState(false)
  const [recognizedText, setRecognizedText] = useState('')
  const [interimText, setInterimText] = useState('')
  const recognition = useRef<any>(null)
  const userStoppedListening = useRef(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])

  const [uploadStatus, setUploadStatus] = useState('')

  const [inputQuestion, setInputQuestion] = useState<string>('')

  const [isSendBtnClicked, setIsSendBtnClicked] = useState(false)

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor | undefined>(undefined)

  const showSnackbar = useSnackbar()
  const navigate = useNavigate()

  useEffect(() => {
    if ('webkitSpeechRecognition' in window) {
      /* eslint-disable new-cap */
      recognition.current = new window.webkitSpeechRecognition()
      const currentRecognition = recognition.current
      /* eslint-enable new-cap */
      currentRecognition.continuous = true
      currentRecognition.interimResults = true

      currentRecognition.onstart = () => {
        // console.log('Recognition started')
        setIsListening(true)
        userStoppedListening.current = false
      }

      currentRecognition.onresult = (event: any) => {
        let finalTranscript = ''
        let interimTranscript = ''
        // console.log('event.results[i][0].transcript;', event.results)
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          const transcript: string = event.results[i][0].transcript
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' '
          } else {
            interimTranscript += transcript
          }
        }
        setRecognizedText((prevText) => prevText + finalTranscript)
        // setInputQuestion((prevText) => prevText + finalTranscript)
        promptRef.current && (promptRef.current.value = finalTranscript + interimTranscript)

        setInterimText(interimTranscript)
      }

      currentRecognition.onend = () => {
        // console.log('Recognition service ended.')
        setIsListening(false)
        // If the user did NOT manually stop it, restart it
        if (!userStoppedListening.current) {
          // console.log('Restarting recognition...')

          currentRecognition.start()
        }
      }

      currentRecognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error)
        setIsListening(false)
        userStoppedListening.current = true
      }

      // Clean up the recognition object on unmount
      return () => {
        currentRecognition.stop()
      }
    } else {
      console.warn('Web Speech API is not supported in this browser.')
    }
  }, [])

  useEffect(() => {
    if (inputMessage?.length > 0) {
      // promptRef.current.value = inputMessage
      setInputQuestion(inputMessage)
    } else {
      // setInputQuestion('')
    }
  }, [inputMessage])

  useEffect(() => {
    if (transcript?.length > 0) {
      // alert('inside useEffect transcript')
      setMessage(transcript)
      setLastAskedQuestion(transcript)
      setInputQuestion('')
    }
  }, [transcript])

  useEffect(() => {
    selectedFiles?.length > 0 && onFileUpload()
  }, [selectedFiles])

  useEffect(() => {
    if (newChatClicked && promptRef && promptRef.current) {
      promptRef.current.value = ''
      setInputQuestion('')
    }
    if (newChatClicked) {
      setShowFileUploadBox(false)
      setOpenSpeechToText(false)
    }
  }, [newChatClicked])

  useEffect(() => {
    if (!isLoading) {
      setIsSendBtnClicked(false)
      // setSendIsLoading(false)
      // setInputQuestion('')
    }
  }, [isLoading])

  useEffect(() => {
    if (!sendIsLoading) {
      setInputQuestion('')
    }
  }, [sendIsLoading])

  const handleMicClick = () => {
    if (isListening) {
      // userStoppedListening.current = true
      // recognition.current?.stop()
      if (recognizedText?.length === 0) {
        setOpenSpeechToText(false)
      }
    } else {
      // alert('inside handleMicClick')
      setOpenSpeechToText(true)
      setInputQuestion('')
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  const handleChange = (event: any) => {
    const { value: inputValue } = event.target
    // inputQuestion?.length === 0 || isSendBtnClicked || isFileUploading
    setInputQuestion(inputValue)
    setIsSendBtnClicked(false)
    // setMessage(inputValue)
    // setLastAskedQuestion(inputValue)
  }

  const handleEnterKeyPress = async (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault()
      const target = event.target as HTMLTextAreaElement
      const start: number = target.selectionStart
      const end: number = target.selectionEnd
      const value: string = target.value

      // Insert newline at cursor position
      const newValue = value.substring(0, start) + '\n' + value.substring(end)

      // Update the state with the new value
      setInputQuestion(newValue)

      // Set cursor position after the newline in the next tick
      setTimeout(() => {
        const newPosition = start + 1
        target.selectionStart = newPosition
        target.selectionEnd = newPosition
      }, 0)
    } else if (event.key === 'Enter') {
      if (isModelBuilding) {
        showSnackbar('Try again after AI finishes the file process', 'info')
        return
      }
      event.preventDefault()
      handleSendClick()
      // Handle regular Enter key press
    }
  }

  const handleSendClick = async () => {
    if (!userId) {
      // console.log('src/pages/ChatBoat/MainContent/SendMessage/index.tsx')
      setSnackbarMessage('Your session has expired please login again')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      setTimeout(() => {
        navigate('/auth/login')
      }, 3500)
    } else if (promptRef?.current?.value?.length === 0) {
      setSnackbarMessage('Input prompt cannot be empty, Enter something!')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      setIsSendBtnClicked(false)
    } else {
      // alert('inside handleSendClick')

      const uStatus: any = await userStatus(userId)
      if (uStatus?.status === false) {
        setSnackbarMessage('Something is wrong with user status!')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
        setSendIsLoading(false)
      } else {
        const promptValue = promptRef.current?.value ?? ''

        const handleSearchCountResponse = await handleSearchCount(promptValue, userId)
        if (handleSearchCountResponse) {
          setIsSendBtnClicked(true)
          setSendIsLoading(true)
          // Hide file upload box on send
          setShowFileUploadBox(false)
          sendData(promptValue)
          setRecognizedText('')
          setInterimText('')
        } else {
          setSendIsLoading(false)
        }
      }
    }
    // setMessage('')
  }

  const handleAttachFile = () => {
    // fileInputRef.current?.click()

    setShowFileUploadBox(!showFileUploadBox)
  }

  const onFileUpload = () => {
    // setIsFileUploading(true)
    // setSendIsLoading(true)
    if (!selectedFiles) {
      setUploadStatus('Please select a file first!')
      setSnackbarMessage('Please select a file first!')
      setSnackbarSeverity('warning')
      setSnackbarOpen(true)
      // setSendIsLoading(false)
      // setIsFileUploading(false)
      return
    }

    const formData = new FormData()

    selectedFiles.forEach((element) => {
      formData.append('file', element)
    })

    const generatedUniqueString = generateUniqueString()
    if (generatedUniqueString) {
      setUniqueString(generatedUniqueString)
      formData.append('uniqueString', generatedUniqueString)

      fetch(`${baseUrl}/pyapi/mainqa`, {
        method: 'POST',
        headers: {
          usecase: 'upload',
        },
        body: formData,
      })
        .then((response) => {
          if (!response.ok) {
            return response.json().then((err) => {
              throw new Error(err.error)
            })
          }
          return response.json()
        })
        .then((data) => {
          // console.log('after upload file:', data)
          // console.log('File successfullly uploaded')
          if (uploadedFileName?.length > 1) {
            showSnackbar('Files successfully uploaded!', 'success')
          }

          if (uploadedFileName?.length === 1) {
            showSnackbar('File successfully uploaded!', 'success')
          }

          // setIsFileUploading(false)
          setIsRefFileUploaded(true)
        })
        .catch((error) => {
          // console.log(`Error: ${error.message}`)
          setUploadStatus(error?.message)

          showSnackbar(
            error?.message || 'Something went wrong! while file uploading... or Check the file',
            'error',
          )
          // setSendIsLoading(false)
          // setIsFileUploading(false)
        })
    }
  }

  const generateUniqueString = () => {
    return 'xxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  // console.log('isRefFileUploaded', isRefFileUploaded)

  return (
    <>
      {/* Sppech to text dialog box */}
      {openSpeechToText && <SpeechToText setInputQuestion={setInputQuestion} />}

      {/* File attachment dialog box */}
      <Box sx={{ display: showFileUploadBox ? 'block' : 'none' }}>
        <FileUpload setShowFileUploadBox={setShowFileUploadBox} />
      </Box>

      <Stack
        spacing={1}
        sx={{
          width: '100%',
        }}
      >
        <Stack
          direction='row'
          alignItems='flex-end'
          flexGrow={1}
          sx={{
            bgcolor: '#fafafa',
          }}
        >
          <Stack flexGrow={1} sx={{ marginRight: { xs: '8px', sm: '16px' } }}>
            {sendIsLoading && <LoadingMessages isLoading={sendIsLoading} />}

            {/* Display attached files chip */}
            {!showFileUploadBox && uploadedFileName?.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Chip
                  label={`${uploadedFileName.length} file${
                    uploadedFileName.length > 1 ? 's' : ''
                  } attached`}
                  onDelete={() => {
                    setShowFileUploadBox(true)
                  }}
                  deleteIcon={
                    <Tooltip title='Maximize' placement='top'>
                      <OpenInFullIcon />
                    </Tooltip>
                  }
                  color='primary'
                  variant='outlined'
                  size='medium'
                  onClick={() => {
                    setShowFileUploadBox(true)
                  }}
                  sx={{
                    fontWeight: 600,
                    cursor: 'pointer',
                    borderColor: '#1976d2',
                    '& .MuiChip-label': {
                      fontWeight: 700,
                      color: '#1976d2',
                    },
                    '& .MuiChip-deleteIcon': {
                      color: 'red',
                      '&:hover': {
                        color: 'green',
                      },
                    },
                  }}
                />
                <Typography sx={{ color: 'green', mt: 0.5, fontSize: '13px' }}>
                  * The attached files are considered part of this prompt, and your prompt/question
                  should be based on them.
                </Typography>
                {/* <Typography sx={{ color: 'green', mt: 0.5, fontSize: '12px' }}>
                  The attached files are considered part of this prompt, and your prompt/question
                  should be based on them. If you do not want these files to be included, please
                  remove them before submitting your question.
                </Typography> */}
              </Box>
            )}

            <StyledInputBase
              disabled={sendIsLoading || isFileUploading}
              inputRef={!isListening ? promptRef : dummyRef}
              multiline
              // rows={1}
              // maxRows={3}
              value={inputQuestion}
              onChange={handleChange}
              onKeyDown={handleEnterKeyPress}
              placeholder={placeholder}
              startAdornment={
                <IconButton
                  onClick={handleAttachFile}
                  disabled={isFileUploading || sendIsLoading || isModelBuilding}
                >
                  <AttachFileIcon />
                </IconButton>
              }
              // endAdornment={isPaid === 'true' ? <MicPhoneButton /> : null}
              endAdornment={
                <IconButton
                  onClick={handleMicClick}
                  color='primary'
                  disabled={isFileUploading || sendIsLoading || isModelBuilding}
                >
                  {isListening ? (
                    <MicOffIcon sx={{ fontSize: 30, color: 'red' }} />
                  ) : (
                    <MicIcon sx={{ fontSize: 30 }} />
                  )}
                </IconButton>
              }
              sx={{
                display: 'flex',
                // alignItems: 'flex-end',

                '& .MuiInputBase-startAdornment, & .MuiInputBase-endAdornment': {
                  alignSelf: 'flex-end',
                  paddingBottom: '4px',
                },

                '& .MuiInputBase-input::placeholder': {
                  color: '#666 !important',
                  opacity: 0.4,
                },
              }}
            />
          </Stack>
          <Button
            variant='contained'
            disabled={
              inputQuestion?.length === 0 || isFileUploading || sendIsLoading || isModelBuilding
            }
            sx={{ minWidth: 50, width: 50, height: 50, p: 0 }}
            onClick={() => {
              handleSendClick() // Optionally, call handleSendClick as well
            }}
          >
            {sendIsLoading ? <CircularProgress color='warning' /> : <SendIcon />}
          </Button>
        </Stack>
        <Typography variant='link' fontWeight='regular'>
          <Typography variant='link' color='error.main'>
            *
          </Typography>{' '}
          This platform may produce inaccurate information, that doesn’t represent Lawttorney views
        </Typography>
      </Stack>

      {snackbarOpen && (
        <Snackbar
          sx={{ zIndex: '10002' }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
        >
          <Alert elevation={6} onClose={handleCloseSnackbar} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  )
}

export default SendMessage
